/**
 * Scheduled Tasks List Component
 *
 * Displays a table of all scheduled data retrieval tasks with:
 * - Task name and description with parent/child hierarchy (accordion tree view)
 * - Associated entity mapping
 * - Schedule information
 * - Status (enabled/disabled)
 * - Last and next execution times
 * - Actions (edit, delete, enable/disable)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import {
  listScheduledTasks,
  deleteScheduledTask,
  enableScheduledTask,
  disableScheduledTask,
  validateScheduleIndex,
  executeScheduledTask,
  cancelScheduledTask,
  getScheduledTaskChildren,
  type ScheduledTask
} from '../../lib/api';
import {
  TaskFilters,
  type TaskFiltersState,
  loadFiltersFromStorage,
  saveFiltersToStorage
} from '../scheduler/TaskFilters';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { Badge } from '../ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Plus, Play, Pause, Trash2, Edit, RefreshCw, PlayCircle, Database, Link2, RefreshCcw, ChevronRight, ChevronDown, Minus, Circle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { formatScheduleWithTimezone, formatDateTime as formatDateTimeUtil } from '../../lib/scheduleFormatter';
import { usePermissions } from '@/contexts/PermissionContext';

interface ScheduledTasksListProps {
  returnPath?: string;
}

export function ScheduledTasksList({ returnPath = '/scheduled-tasks' }: ScheduledTasksListProps = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [filters, setFilters] = useState<TaskFiltersState>(loadFiltersFromStorage);
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('scheduler', 'edit');
  const canExecute = hasPermission('scheduler', 'execute');
  const noEditMessage = "You don't have permission to edit scheduled tasks";
  const noExecuteMessage = "You don't have permission to run or validate tasks";

  // State for accordion tree view
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [childTasksCache, setChildTasksCache] = useState<Map<string, ScheduledTask[]>>(new Map());
  const [loadingChildren, setLoadingChildren] = useState<Set<string>>(new Set());

  // Track which specific task is being executed/validated
  const [executingTaskId, setExecutingTaskId] = useState<string | null>(null);
  const [validatingTaskId, setValidatingTaskId] = useState<string | null>(null);

  const ensureCanEdit = () => {
    if (!canEdit) {
      toast({
        title: "Access denied",
        description: noEditMessage,
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const ensureCanExecute = () => {
    if (!canExecute) {
      toast({
        title: "Access denied",
        description: noExecuteMessage,
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  // Save filters to localStorage when they change (except search)
  useEffect(() => {
    saveFiltersToStorage(filters);
  }, [filters.status, filters.sort]);

  // Fetch scheduled tasks
  const { data, isLoading, error } = useQuery({
    queryKey: ['scheduledTasks'],
    queryFn: listScheduledTasks,
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Clear child cache when task data is refetched (to avoid stale child counts)
  const taskDataUpdatedAt = data?.tasks?.length ?? 0;
  useEffect(() => {
    // When the parent task list changes, invalidate the child cache
    // This handles cases where children were added/removed via the form
    setChildTasksCache(new Map());
  }, [taskDataUpdatedAt]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteScheduledTask,
    onSuccess: (_, deletedTaskId) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledTasks'] });
      // Clear the child cache in case a parent was deleted
      setChildTasksCache(prev => {
        const next = new Map(prev);
        next.delete(deletedTaskId);
        // Also remove from any parent's children list
        next.forEach((children, parentId) => {
          const filtered = children.filter((c) => c.id !== deletedTaskId);
          if (filtered.length !== children.length) {
            next.set(parentId, filtered);
          }
        });
        return next;
      });
      toast({
        title: 'Success',
        description: 'Scheduled task deleted successfully',
      });
      setTaskToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to delete task: ${error.message}`,
        variant: 'destructive'
      });
    }
  });

  // Enable mutation
  const enableMutation = useMutation({
    mutationFn: enableScheduledTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledTasks'] });
      toast({
        title: 'Success',
        description: 'Scheduled task enabled',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to enable task: ${error.message}`,
        variant: 'destructive'
      });
    }
  });

  // Disable mutation
  const disableMutation = useMutation({
    mutationFn: disableScheduledTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledTasks'] });
      toast({
        title: 'Success',
        description: 'Scheduled task disabled',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to disable task: ${error.message}`,
        variant: 'destructive'
      });
    }
  });

  // Validate schedule index mutation
  const validateMutation = useMutation({
    mutationFn: validateScheduleIndex,
    onSuccess: (data) => {
      setValidatingTaskId(null);
      queryClient.invalidateQueries({ queryKey: ['scheduledTasks'] });

      if (data.fixed) {
        toast({
          title: 'Schedule Index Repaired',
          description: data.message,
        });
      } else if (data.valid) {
        toast({
          title: 'Schedule Index Valid',
          description: data.message,
        });
      } else {
        toast({
          title: 'Validation Issue',
          description: data.message,
          variant: 'destructive'
        });
      }
    },
    onError: (error: Error) => {
      setValidatingTaskId(null);
      toast({
        title: 'Error',
        description: `Failed to validate schedule index: ${error.message}`,
        variant: 'destructive'
      });
    }
  });

  // Execute Now mutation
  const executeMutation = useMutation({
    mutationFn: executeScheduledTask,
    onSuccess: () => {
      setExecutingTaskId(null);
      queryClient.invalidateQueries({ queryKey: ['scheduledTasks'] });
      toast({
        title: 'Task Execution Started',
        description: 'The scheduled task has been queued for immediate execution',
      });
    },
    onError: (error: Error) => {
      setExecutingTaskId(null);
      toast({
        title: 'Error',
        description: `Failed to execute task: ${error.message}`,
        variant: 'destructive'
      });
    }
  });

  // Cancel stuck task mutation
  const cancelMutation = useMutation({
    mutationFn: cancelScheduledTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledTasks'] });
      toast({
        title: 'Task Cancelled',
        description: 'The stuck task has been reset to pending status',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to cancel task: ${error.message}`,
        variant: 'destructive'
      });
    }
  });

  const formatSchedule = (task: ScheduledTask): string => {
    return formatScheduleWithTimezone(task.schedule);
  };

  const formatDateTime = (dateString?: string, timezone?: string): string => {
    return formatDateTimeUtil(dateString, timezone);
  };

  // Toggle expand/collapse for parent tasks
  // Use refs to avoid race conditions and stale closures
  const loadingChildrenRef = React.useRef(loadingChildren);
  const childTasksCacheRef = React.useRef(childTasksCache);
  loadingChildrenRef.current = loadingChildren;
  childTasksCacheRef.current = childTasksCache;

  const toggleExpand = useCallback(async (taskId: string) => {
    // Use functional update to check current expanded state
    let wasExpanded = false;
    setExpandedTasks(prev => {
      wasExpanded = prev.has(taskId);
      if (wasExpanded) {
        // Collapse
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      } else {
        // Expand
        return new Set(prev).add(taskId);
      }
    });

    // If we're expanding and don't have children cached, fetch them
    if (!wasExpanded) {
      // Check refs to avoid stale closure issues
      const isAlreadyLoading = loadingChildrenRef.current.has(taskId);
      const hasCache = childTasksCacheRef.current.has(taskId);

      if (!hasCache && !isAlreadyLoading) {
        setLoadingChildren(prev => new Set(prev).add(taskId));
        try {
          const response = await getScheduledTaskChildren(taskId);
          if (!response || !Array.isArray(response.children)) {
            throw new Error('Invalid response format');
          }
          setChildTasksCache(prev => new Map(prev).set(taskId, response.children));
        } catch (error) {
          console.error('Failed to fetch child tasks:', error);
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to load child tasks',
            variant: 'destructive'
          });
          // Collapse on error
          setExpandedTasks(prev => {
            const next = new Set(prev);
            next.delete(taskId);
            return next;
          });
        } finally {
          setLoadingChildren(prev => {
            const next = new Set(prev);
            next.delete(taskId);
            return next;
          });
        }
      }
    }
  }, [toast]);

  // Helper to check if a task is a parent (not a child task)
  const isParentOrStandalone = (task: ScheduledTask) => !task.parentTaskId;

  // Helper to check if a task has children
  const hasChildren = (task: ScheduledTask) => (task.childCount ?? 0) > 0;

  const getTaskTypeDisplay = (task: ScheduledTask): { label: string; type: 'legacy' | 'sql-sync' | 'metadata' } => {
    // Check if it's a job-based task
    if (task.jobType) {
      const params = (task.parameters || {}) as Record<string, unknown>;

      if (task.jobType === 'Sql.EntitySync') {
        // Try to get entityType from parameters, or extract from task name
        let entityType = params.entityType as string;
        if (!entityType && task.name) {
          // Try to extract from name like "SQL Sync: Title" or "SQL Sync: Series"
          const match = task.name.match(/SQL Sync[:\s]+(\w+)/i);
          if (match) {
            entityType = match[1];
          }
        }
        return {
          label: `SQL Sync: ${entityType || 'Entity'}`,
          type: 'sql-sync'
        };
      }

      if (task.jobType === 'Sql.RefreshMetadata') {
        const entityType = params.entityType as string;
        return {
          label: entityType ? `Metadata: ${entityType}` : 'Metadata Refresh',
          type: 'metadata'
        };
      }

      if (task.jobType === 'Sql.FieldSync') {
        let entityType = params.entityType as string;
        if (!entityType && task.name) {
          const match = task.name.match(/Field Sync[:\s]+(\w+)/i);
          if (match) {
            entityType = match[1];
          }
        }
        return {
          label: `Field Sync: ${entityType || 'Entity'}`,
          type: 'sql-sync'
        };
      }

      // Generic job type
      return {
        label: task.jobType,
        type: 'sql-sync'
      };
    }

    // Legacy entity-mapping task (deprecated)
    if (task.entityMapping) {
      return { label: 'Legacy Entity Mapping (deprecated)', type: 'legacy' };
    }

    return { label: task.name || 'Unknown task', type: 'legacy' };
  };

  const getExecutionStatus = (task: ScheduledTask): {
    status: 'success' | 'failed' | 'never' | 'running';
    message?: string;
    icon: React.ReactNode;
    variant: 'default' | 'destructive' | 'secondary' | 'outline';
  } => {
    // Execution history moved to separate table - show last execution time instead
    if (!task.lastExecutionTime) {
      return {
        status: 'never',
        message: 'No executions yet',
        icon: <Circle className="w-2.5 h-2.5 text-muted-foreground/60" />,
        variant: 'secondary'
      };
    }

    // Without embedded history, we can only show that task has run before
    // Full execution details available via task execution history page
    return {
      status: 'success',
      message: `Last run: ${formatDateTime(task.lastExecutionTime)}`,
      icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
      variant: 'default'
    };
  };

  const allTasks = data?.tasks || [];

  // Filter to only parent/standalone tasks (children shown when parent expanded)
  const parentAndStandaloneTasks = useMemo(() => {
    return allTasks.filter(isParentOrStandalone);
  }, [allTasks]);

  // Apply filtering and sorting - must be before any early returns to follow Rules of Hooks
  const filteredAndSortedTasks = useMemo(() => {
    let result = [...parentAndStandaloneTasks];

    // Apply search filter
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      result = result.filter(task =>
        task.name?.toLowerCase().includes(searchLower) ||
        task.description?.toLowerCase().includes(searchLower) ||
        getTaskTypeDisplay(task).label.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status !== 'all') {
      result = result.filter(task => {
        const isEnabled = task.schedule?.enabled ?? task.isEnabled ?? false;
        return filters.status === 'enabled' ? isEnabled : !isEnabled;
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (filters.sort) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'nextRun':
          if (!a.nextExecutionTime && !b.nextExecutionTime) return 0;
          if (!a.nextExecutionTime) return 1;
          if (!b.nextExecutionTime) return -1;
          return new Date(a.nextExecutionTime).getTime() - new Date(b.nextExecutionTime).getTime();
        case 'lastRun':
          if (!a.lastExecutionTime && !b.lastExecutionTime) return 0;
          if (!a.lastExecutionTime) return 1;
          if (!b.lastExecutionTime) return -1;
          return new Date(b.lastExecutionTime).getTime() - new Date(a.lastExecutionTime).getTime();
        case 'created':
          if (!a.createdOn && !b.createdOn) return 0;
          if (!a.createdOn) return 1;
          if (!b.createdOn) return -1;
          return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [parentAndStandaloneTasks, filters]);

  const tasks = filteredAndSortedTasks;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-800">
          Failed to load scheduled tasks: {error.message}
        </p>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-2">
        <div className="space-y-2">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold dark:text-zinc-100">Scheduled Tasks</h2>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled
                title="Task groups coming soon"
                className="opacity-60 cursor-not-allowed"
              >
                <Plus className="mr-1 h-4 w-4" />
                New Group
              </Button>
              <Link href="/scheduled-tasks/new">
                <Button
                  disabled={!canEdit}
                  title={!canEdit ? noEditMessage : undefined}
                  className={`h-9 px-3 ${!canEdit ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  New Task
                </Button>
              </Link>
            </div>
          </div>

          {parentAndStandaloneTasks.length === 0 ? (
            /* No tasks at all - show empty state */
            <div className="rounded-lg border border-dashed p-8 text-center">
              <p className="text-sm text-muted-foreground mb-4">
                No scheduled tasks configured yet.
              </p>
              <Link href="/scheduled-tasks/new">
                <Button
                  disabled={!canEdit}
                  title={!canEdit ? noEditMessage : undefined}
                  className={!canEdit ? "opacity-50 cursor-not-allowed" : ""}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First Scheduled Task
                </Button>
              </Link>
            </div>
          ) : (
        <>
          {/* Filters - only show when there are tasks */}
          <TaskFilters
            filters={filters}
            onFiltersChange={setFilters}
            taskCount={parentAndStandaloneTasks.length}
            filteredCount={tasks.length}
          />

          {tasks.length === 0 ? (
            /* Tasks exist but none match filters */
            <div className="rounded-lg border border-dashed p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No tasks match your current filters.
              </p>
            </div>
          ) : (
          <div className="rounded-md border">
            <Table className="text-sm">
            <TableHeader>
              <TableRow className="h-auto">
                <TableHead className="py-2">Task</TableHead>
                <TableHead className="py-2">Type</TableHead>
                <TableHead className="py-2">Timing</TableHead>
                <TableHead className="py-2 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map((task) => {
                const executionStatus = getExecutionStatus(task);
                const taskTypeDisplay = getTaskTypeDisplay(task);
                const TaskTypeIcon = taskTypeDisplay.type === 'sql-sync' ? Database
                  : taskTypeDisplay.type === 'metadata' ? RefreshCcw
                  : Link2;
                const isExpanded = expandedTasks.has(task.id);
                const taskHasChildren = hasChildren(task);
                const isLoadingThisTask = loadingChildren.has(task.id);
                const childTasks = childTasksCache.get(task.id) || [];
                const isEnabled = task.schedule?.enabled ?? task.isEnabled ?? false;

                return (
                  <React.Fragment key={task.id}>
                    {/* Parent/Standalone Task Row */}
                    <TableRow className="hover:bg-muted/50 h-auto">
                      <TableCell className="py-2">
                        <div className="flex items-start gap-2">
                          {taskHasChildren ? (
                            <button
                              onClick={() => toggleExpand(task.id)}
                              className="p-0.5 hover:bg-muted rounded"
                              disabled={isLoadingThisTask}
                            >
                              {isLoadingThisTask ? (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              ) : isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                            </button>
                          ) : (
                            <Minus className="h-4 w-4 text-muted-foreground/50" />
                          )}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{task.name || 'Unnamed Task'}</span>
                              <span className={`h-2 w-2 rounded-full ${isEnabled ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                              {taskHasChildren && (
                                <span className="text-[11px] text-muted-foreground">
                                  {task.childCount} child{(task.childCount ?? 0) !== 1 ? 'ren' : ''}
                                </span>
                              )}
                            </div>
                            {task.description && (
                              <div className="text-xs text-muted-foreground line-clamp-1">
                                {task.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2 text-sm">
                          <TaskTypeIcon className="h-4 w-4 text-muted-foreground" />
                          {taskHasChildren ? 'Parent Task' : taskTypeDisplay.label}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                          <div className="flex items-center gap-2 text-sm text-foreground">
                            {executionStatus.icon}
                            {formatSchedule(task) || 'No schedule'}
                            {executionStatus.status === 'success' && <span className="text-xs text-emerald-600">✓</span>}
                            {executionStatus.status === 'failed' && <span className="text-xs text-red-600">✗</span>}
                            {executionStatus.status === 'running' && <span className="text-xs text-blue-600">⟳</span>}
                            {executionStatus.status === 'never' && <span className="text-xs text-muted-foreground">◌</span>}
                          </div>
                          <div className="flex items-center gap-3">
                            <span>Last: {formatDateTime(task.lastExecutionTime) || '—'}</span>
                            <span>Next: {isEnabled ? (formatDateTime(task.nextExecutionTime) || '—') : '—'}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right py-2">
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (!ensureCanExecute()) return;
                                    setExecutingTaskId(task.id);
                                    executeMutation.mutate(task.id);
                                  }}
                                  disabled={executingTaskId !== null || !canExecute}
                                  title={!canExecute ? noExecuteMessage : undefined}
                                >
                                  <PlayCircle className={`h-4 w-4 ${executingTaskId === task.id ? 'animate-spin' : ''}`} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Run now (immediate execution)</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          {/* Cancel button - shown when task is stuck (running status or stale claimedBy) */}
                          {(task.status === 'running' || task.claimedBy) && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      if (!ensureCanExecute()) return;
                                      cancelMutation.mutate(task.id);
                                    }}
                                    disabled={cancelMutation.isPending || !canExecute}
                                    title={!canExecute ? noExecuteMessage : undefined}
                                  >
                                    <XCircle className="h-4 w-4 text-orange-500" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Reset stuck task (clear claim and set to pending)</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (!ensureCanExecute()) return;
                                    setValidatingTaskId(task.id);
                                    validateMutation.mutate(task.id);
                                  }}
                                  disabled={validatingTaskId !== null || !canExecute}
                                  title={!canExecute ? noExecuteMessage : undefined}
                                >
                                  <RefreshCw className={`h-4 w-4 ${validatingTaskId === task.id ? 'animate-spin' : ''}`} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Validate & repair schedule index</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Link href={`/scheduled-tasks/${task.id}/edit?returnPath=${encodeURIComponent(returnPath)}`}>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={!canEdit}
                                    title={!canEdit ? noEditMessage : undefined}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </Link>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Edit task</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          {isEnabled ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!ensureCanEdit()) return;
                                disableMutation.mutate(task.id);
                              }}
                              disabled={disableMutation.isPending || !canEdit}
                              title={!canEdit ? noEditMessage : undefined}
                            >
                              <Pause className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!ensureCanEdit()) return;
                                enableMutation.mutate(task.id);
                              }}
                              disabled={enableMutation.isPending || !canEdit}
                              title={!canEdit ? noEditMessage : undefined}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (!ensureCanEdit()) return;
                              setTaskToDelete(task.id);
                            }}
                            disabled={!canEdit}
                            title={!canEdit ? noEditMessage : undefined}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Child Task Rows (when expanded) */}
                    {isExpanded && childTasks.map((child, childIndex) => {
                      const childExecutionStatus = getExecutionStatus(child);
                      const childTaskTypeDisplay = getTaskTypeDisplay(child);
                      const ChildTaskTypeIcon = childTaskTypeDisplay.type === 'sql-sync' ? Database
                        : childTaskTypeDisplay.type === 'metadata' ? RefreshCcw
                        : Link2;
                      const isLastChild = childIndex === childTasks.length - 1;
                      const status = (child as any).status as string | undefined;
                      const isRunning = status === 'running';
                      const isFailed = status === 'failed';

                      return (
                        <TableRow key={child.id} className="bg-muted/30 hover:bg-muted/50 h-auto leading-tight">
                          <TableCell className="py-0.5">
                            <div className="flex items-center gap-1 pl-3">
                              {/* Tree line indicator */}
                              <span className="text-muted-foreground font-mono text-xs w-3">
                                {isLastChild ? '└' : '├'}
                              </span>
                              <span className="text-muted-foreground text-xs w-4 text-right">
                                {child.executionOrder ?? childIndex + 1}.
                              </span>
                              <div>
                                <div className="font-medium text-sm">{child.name || 'Unnamed Task'}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-0.5">
                            <div className="flex items-center gap-1 text-xs">
                              <ChildTaskTypeIcon className="h-3 w-3 text-muted-foreground" />
                              {childTaskTypeDisplay.label}
                            </div>
                          </TableCell>
                          <TableCell className="py-0.5">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      {childExecutionStatus.icon}
                                      {isRunning && <span className="text-blue-600">⟳</span>}
                                      {isFailed && <span className="text-red-600">✗</span>}
                                      {childExecutionStatus.status === 'success' && <span className="text-emerald-600">✓</span>}
                                    </span>
                                    <span>Last: {formatDateTime(child.lastExecutionTime) || '—'}</span>
                                    {isRunning && (
                                      <div className="h-1 w-12 rounded-full bg-muted overflow-hidden">
                                        <div className="h-full w-1/2 bg-primary animate-pulse" />
                                      </div>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{childExecutionStatus.message}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell className="text-right py-0.5 text-xs text-muted-foreground">
                            {(child.status === 'running' || child.claimedBy) && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => {
                                        if (!ensureCanExecute()) return;
                                        cancelMutation.mutate(child.id);
                                      }}
                                      disabled={cancelMutation.isPending || !canExecute}
                                      title={!canExecute ? noExecuteMessage : undefined}
                                    >
                                      <XCircle className="h-3.5 w-3.5 text-orange-500" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Reset stuck task (clear claim and set to pending)</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!taskToDelete} onOpenChange={() => setTaskToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Scheduled Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this scheduled task? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => taskToDelete && canEdit && deleteMutation.mutate(taskToDelete)}
              className="bg-red-600 hover:bg-red-700"
              disabled={!canEdit}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
